import React, {
  useRef,
  useReducer,
  useCallback,
  useMemo,
  useState,
} from "react";
import { View, StyleSheet, Pressable, TouchableOpacity } from "react-native";
import Icon from "../../../Icon";
import RText from "../../../RText";
import variables from "../../../../styles/variables";
import { formatRaceTime } from "../../../../utils/TimeCalculator";
import { useResponsive } from "../../../../hooks/useResponsive";
import { Meeting as MeetingT } from "../../../../types/races/meeting";
import AnimatedDropdownWrapper from "../../../Animations/DropdownAnimation";
import Gap from "../../../Gap";
import RaceRow from "../../../Racecard/RaceRow";
import { EmptyState } from "../../../States/states";
import { getRaceType } from "../../../../utils/race";
import { fetchRaceNotifications } from "../../../../utils/raceNotifications";
import { removeBracketsContainingPhrase } from "../../../../utils/removeBracketsContainingPhrase";

type MeetingProps = {
  meeting: MeetingT;
  track: { name: string; slug: string; country_code?: { iso: string } };
  i?: number;
  headerOverride?: string;
  fetchRaceDetails?: boolean;
  showWeatherIcon?: boolean;
  forceOpen?: boolean;
};

/**
 * Meeting component
 *
 * Displays a collapsible meeting section with track details, weather, and a list of races.
 * The header shows track info, weather icon, and a chevron to expand/collapse the race list.
 * Responsive: adapts details for small screens.
 *
 * @component
 * @param {Object} props - Component props
 * @param {MeetingT} props.meeting - Meeting data including races and track info
 * @param {{ name: string, slug: string }} props.track - Track information
 * @param {number} [props.i] - Index of the meeting in the list
 * @param {string} [props.headerOverride] - Optional override for the header text
 * @param {boolean} [props.fetchRaceDetails] - Whether to fetch race details on expand
 * @param {boolean} [props.showWeatherIcon] - Whether to show the weather icon
 * @param {boolean} [props.forceOpen] - Whether the meeting should be open by default
 * @returns {JSX.Element} The rendered Meeting component
 */
const Meeting = ({
  meeting,
  i,
  headerOverride = undefined,
  track,
  fetchRaceDetails = false,
  showWeatherIcon = true,
  forceOpen = false,
}: MeetingProps): JSX.Element => {
  const { isSmallAndDown } = useResponsive();
  const races = meeting.races;
  const [raceNotifications, setRaceNotifications] = useState<number[]>([]);

  // Use a ref for the open flag and a reducer to force a re-render when toggling.
  const openRef = useRef(forceOpen);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const toggleOpen = useCallback(() => {
    openRef.current = !openRef.current;
    if (openRef.current) {
      fetchRaceNotifications(races)
        .then((res) => setRaceNotifications(res))
        .catch((err) => console.log("error setting race notifications", err));
    }
    forceUpdate();
  }, [races]);

  // Memoize the meeting details (used in header and below) so they don't recalc on open toggle.
  const MeetingDetails = useMemo(() => {
    if (!races?.length) return null;
    const raceTypes = meeting.races.map((race) => race.race_type);
    const raceType = getRaceType(raceTypes);
    const firstRaceTime = formatRaceTime(races[0]?.start_time_scheduled);
    const lastRaceTime = formatRaceTime(
      races[races.length - 1]?.start_time_scheduled
    );
    return (
      <View style={styles.meetingDetails}>
        <RText style={{ textAlign: "center" }}>
          {[
            raceType,
            `${meeting.races.length} Races`,
            `${firstRaceTime}-${lastRaceTime}`,
            isSmallAndDown
              ? meeting.track_condition
              : removeBracketsContainingPhrase(
                  meeting.track_condition,
                  "in places"
                ),
          ]
            .filter((x) => x != null)
            .join(", ")}
        </RText>
      </View>
    );
  }, [races, meeting]);

  const hasCountryCode = Boolean(
    meeting?.track?.country_code?.iso || track?.country_code?.iso
  );
  // Memoize the header so that changes to openRef (via forceUpdate) don’t re-run its internals.
  const MemoizedHeader = useMemo(() => {
    return (
      <TouchableOpacity
        style={[styles.headerContainer, i !== 0 && styles.topMargin]}
        onPress={toggleOpen}
      >
        <View style={styles.headerRow}>
          {hasCountryCode ? (
            <Icon
              name={`flag_${
                meeting?.track?.country_code?.iso || track?.country_code?.iso
              }`}
              size="large"
            />
          ) : (
            !headerOverride && <Gap size="large" />
          )}
          {showWeatherIcon &&
            (!!meeting.weather_icon ? (
              <View style={{ flexDirection: "row" }}>
                <Icon name={meeting.weather_icon} size="xlarge" />
                <Gap size="xxsmall" />
              </View>
            ) : (
              <>
                <Gap size="large" />
                <Gap size="xxsmall" />
              </>
            ))}
          <RText family="secondary" weight="bold" size="md">
            {headerOverride || meeting?.track?.name.toUpperCase()}
          </RText>
          {meeting?.track?.tv_channel === "RTV" && (
            <Icon
              name="rLogo"
              size="small"
              fill="palette.neutral.x0"
              style={styles.iconLeftMargin}
            />
          )}
        </View>
        <View style={styles.headerRow}>
          {!isSmallAndDown && MeetingDetails}
          <View style={styles.iconLeftMargin}>
            <ChevronButton open={openRef.current} />
          </View>
        </View>
      </TouchableOpacity>
    );
  }, [
    i,
    meeting,
    headerOverride,
    showWeatherIcon,
    isSmallAndDown,
    openRef.current,
    toggleOpen,
    MeetingDetails,
  ]);

  if (!races) return null;

  return (
    <View style={{ width: "100%" }}>
      {MemoizedHeader}
      <AnimatedDropdownWrapper open={openRef.current}>
        {isSmallAndDown && MeetingDetails}
        {races && races.length ? (
          races.map((race, idx) => (
            <RaceRow
              track={meeting.track || track}
              date={meeting.date}
              race={race}
              key={idx}
              firstRace={idx === 0}
              isRaceNotified={raceNotifications?.includes(race.id)}
              fetchRaceDetails={fetchRaceDetails}
            />
          ))
        ) : (
          <EmptyState
            message="no races available"
            style={{ marginVertical: variables.spacing.xsmall }}
          />
        )}
      </AnimatedDropdownWrapper>
    </View>
  );
};

const ChevronButton = ({ open }) => (
  <Icon
    name="chevronBoldUp"
    size="small"
    fill="palette.neutral.x0"
    style={[styles.icon]}
    rotate={open ? 0 : 180}
  />
);

const { spacing, colors } = variables;
const styles = StyleSheet.create({
  alignCenter: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
  },
  icon: {
    alignSelf: "center",
  },

  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    paddingHorizontal: spacing.xsmall,
    height: 40,
    backgroundColor: colors.palette.neutral.x100,
    userSelect: "none",
  },
  topMargin: {
    marginTop: 5,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  meetingDetails: {
    alignItems: "center",
    justifyContent: "center",
  },
  rowContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: spacing.xxsmall,
    borderTopWidth: 1,
    borderTopColor: colors.palette.neutral.x100,
  },
  col1: {
    width: "10%",
    justifyContent: "flex-start",
  },
  col2: {
    width: "80%",
    paddingHorizontal: spacing.xsmall,
  },
  col3: {
    flexDirection: "row",
    alignSelf: "center",
    justifyContent: "flex-end",
  },
  col3SmallScreen: {
    width: "10%",
  },
  col3BigScreen: {
    width: "3%",
  },
  runnerDetails: {
    flexDirection: "row",
  },
  iconMargin: {
    marginHorizontal: spacing.xsmall,
  },
  iconLeftMargin: {
    marginLeft: spacing.xxsmall,
  },
  iconRightMargin: {
    marginRight: spacing.xxsmall,
  },
  label: {
    backgroundColor: "black",
    borderRadius: 4,
    width: "fit-content",
    paddingVertical: spacing.xxxsmall,
    paddingHorizontal: spacing.xxsmall,
  },
});

export default Meeting;
